import {
  FC,
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from 'react';

import {
  ItemsSearchFilters,
  useItemsSearchFilters,
} from '~hooks/useItemsSearchFilters';
import { useToggle } from '~hooks/useToggle';
import { VehicleBase } from '~services/vehicle/types';
import { DriverCompanyBase } from '~services/driver/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { RouteUserBasic } from '~services/route/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { SelectOption, SelectOptionWithImage } from '~globals/types/commons';

import { useDashboardVariantV2CarrierContext } from './Context';
import {
  DashboardVariantV2CarrierFiltersContextValue,
  DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef,
} from './types';

export const DEFAULT_FILTERS_STATE: ItemsSearchFilters = {
  // COMMONS
  search: '',
  expirationDateTimeFrom: null,
  expirationDateTimeTo: null,
  includeWithoutExpirationDate: null,
  dateFrom: null,
  dateTo: null,
  // SPECIFIC ITEMS
  finalizedDateTimeFrom: null,
  finalizedDateTimeTo: null,
  unit1Gt: '',
  unit1Lt: '',
  unit2Gt: '',
  unit2Lt: '',
  priceGt: '',
  priceLt: '',
  vehicles: null,
  drivers: null,
  services: null,
  zones: null,
  assignedUser: null,
  sourceImports: null,
  itemState: null,
  // SPECIFIC COLLECT
  giverCompany: null,
  driver: null,
  collectState: null,
};

const DashboardVariantV2CarrierCollectFiltersContext =
  createContext<DashboardVariantV2CarrierFiltersContextValue>({
    autocompletesRefs: {
      vehicles: { current: null },
      drivers: { current: null },
      services: { current: null },
      logisticsZones: { current: null },
      assignedUser: { current: null },
      sourceImports: { current: null },
      giverCompany: { current: null },
      driver: { current: null },
      itemState: { current: null },
      collectState: { current: null },
    },
    filters: {
      isFilterApplied: false,
      setIsFilterApplied: () => {},
      filters: DEFAULT_FILTERS_STATE,
      setFilterByKey: () => {},
      handleChangeInputFilter: () => {},
      clearFilters: () => {},
      filtersActives: DEFAULT_FILTERS_STATE,
      setFiltersActives: () => {},
      setFilterActiveByKey: () => {},
      clearFiltersActives: () => {},
      handleApplyFilter: () => {},
      handleResetFilters: () => {},
      handleClearFilters: () => {},
      handleClearFilterByKey: () => {},
      handleClearFilterByValues: () => {},
    },
    forceRequest: { value: false, toggleValue: () => {} },
  });

export const useDashboardVariantV2CarrierCollectFiltersContext =
  (): DashboardVariantV2CarrierFiltersContextValue =>
    useContext(DashboardVariantV2CarrierCollectFiltersContext);

const DashboardVariantV2CarrierCollectFiltersProvider: FC = ({ children }) => {
  const {
    ignoreRefreshBySocket: { toggleValue: toggleIgnoreRefreshBySocket },
    expandedHasChangeOrdersAlert: {
      toggleValue: toggleExpandedHasChangeOrdersAlert,
    },
    modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
  } = useDashboardVariantV2CarrierContext();

  const vehiclesAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        VehicleBase,
        true
      >
    >(null);

  const driversAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        DriverCompanyBase,
        true
      >
    >(null);

  const servicesAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        SelectOption,
        true
      >
    >(null);

  const logiticsZonesAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        LogisticZoneBase,
        true
      >
    >(null);

  const assignedUserAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        RouteUserBasic,
        false
      >
    >(null);

  const sourceImportsAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        SelectOptionWithImage,
        true
      >
    >(null);

  const giverCompanyAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        AuthExtendedCompany,
        false
      >
    >(null);

  const driverAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        DriverCompanyBase,
        false
      >
    >(null);

  const itemStateAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        SelectOption,
        true
      >
    >(null);

  const collectStateAutocompleteRef =
    useRef<
      DashboardVariantV2CarrierFiltersContextCustomAutocompleteRef<
        SelectOption,
        true
      >
    >(null);

  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const filters = useItemsSearchFilters(DEFAULT_FILTERS_STATE);

  const [forceRequest, toggleForceRequest] = useToggle(false);

  const handleApplyFilter = useCallback(() => {
    filters.setFiltersActives(filters.filters);

    setIsFilterApplied(true);

    toggleExpandedHasChangeOrdersAlert(false);
    toggleIgnoreRefreshBySocket(false);
    setModifiedItemsIdBySocket([]);

    toggleForceRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.filters,
    filters.setFiltersActives,
    setIsFilterApplied,
    toggleExpandedHasChangeOrdersAlert,
    toggleIgnoreRefreshBySocket,
    setModifiedItemsIdBySocket,
    toggleForceRequest,
  ]);

  const handleResetFilters = useCallback(() => {
    filters.handleChangeFilters?.(filters.filtersActives);

    vehiclesAutocompleteRef.current?.setValue(
      filters.filtersActives.vehicles ?? [],
    );
    driversAutocompleteRef.current?.setValue(
      filters.filtersActives.drivers ?? [],
    );
    servicesAutocompleteRef.current?.setValue(
      filters.filtersActives.services ?? [],
    );
    logiticsZonesAutocompleteRef.current?.setValue(
      filters.filtersActives.zones ?? [],
    );
    assignedUserAutocompleteRef.current?.setValue(
      filters.filtersActives.assignedUser ?? null,
    );
    sourceImportsAutocompleteRef.current?.setValue(
      filters.filtersActives.sourceImports ?? [],
    );
    giverCompanyAutocompleteRef.current?.setValue(
      filters.filtersActives.giverCompany ?? null,
    );
    driverAutocompleteRef.current?.setValue(
      filters.filtersActives.driver ?? null,
    );
    itemStateAutocompleteRef.current?.setValue(
      filters.filtersActives.itemState ?? [],
    );
    collectStateAutocompleteRef.current?.setValue(
      filters.filtersActives.collectState ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.filters,
    filters.handleChangeInputFilter,
    filters.filtersActives,
  ]);

  const handleClearFilters = useCallback(
    (forceRequestParam: boolean) => {
      filters.clearFilters(DEFAULT_FILTERS_STATE);
      filters.clearFiltersActives();

      vehiclesAutocompleteRef.current?.resetAutocomplete();
      driversAutocompleteRef.current?.resetAutocomplete();
      servicesAutocompleteRef.current?.resetAutocomplete();
      logiticsZonesAutocompleteRef.current?.resetAutocomplete();
      assignedUserAutocompleteRef.current?.resetAutocomplete();
      sourceImportsAutocompleteRef.current?.resetAutocomplete();
      giverCompanyAutocompleteRef.current?.resetAutocomplete();
      driverAutocompleteRef.current?.resetAutocomplete();
      itemStateAutocompleteRef.current?.resetAutocomplete();
      collectStateAutocompleteRef.current?.resetAutocomplete();

      setIsFilterApplied(false);

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);

      if (forceRequestParam) {
        toggleForceRequest(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.clearFilters,
      filters.clearFiltersActives,
      setIsFilterApplied,
      toggleExpandedHasChangeOrdersAlert,
      toggleIgnoreRefreshBySocket,
      setModifiedItemsIdBySocket,
      toggleForceRequest,
    ],
  );

  const handleClearFilterByKey = useCallback(
    (
      key: string,
      newVal: unknown,
      callback?: (newFilters: Partial<ItemsSearchFilters>) => void,
    ) => {
      filters.setFilterByKey(key, newVal);
      filters.setFilterActiveByKey(key, newVal);

      callback?.({ [key]: newVal });

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.setFilterByKey, filters.setFilterActiveByKey],
  );

  const handleClearFilterByValues = useCallback(
    (
      values: Record<string, unknown>,
      callback?: (newFilters: Partial<ItemsSearchFilters>) => void,
    ) => {
      Object.entries(values).map(([key, val]) => {
        filters.setFilterByKey(key, val);
        filters.setFilterActiveByKey(key, val);
      });

      callback?.(values);

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.setFilterByKey, filters.setFilterActiveByKey],
  );

  return (
    <DashboardVariantV2CarrierCollectFiltersContext.Provider
      value={{
        autocompletesRefs: {
          vehicles: vehiclesAutocompleteRef,
          drivers: driversAutocompleteRef,
          services: servicesAutocompleteRef,
          logisticsZones: logiticsZonesAutocompleteRef,
          assignedUser: assignedUserAutocompleteRef,
          sourceImports: sourceImportsAutocompleteRef,
          giverCompany: giverCompanyAutocompleteRef,
          driver: driverAutocompleteRef,
          itemState: itemStateAutocompleteRef,
          collectState: collectStateAutocompleteRef,
        },
        filters: {
          ...filters,
          isFilterApplied,
          setIsFilterApplied,
          handleApplyFilter,
          handleResetFilters,
          handleClearFilters,
          handleClearFilterByKey,
          handleClearFilterByValues,
        },
        forceRequest: {
          value: forceRequest,
          toggleValue: toggleForceRequest,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierCollectFiltersContext.Provider>
  );
};

export default DashboardVariantV2CarrierCollectFiltersProvider;
